"use client";

import React from "react";
import { HardDrive, Cloud, Terminal, Zap, Shield, SlidersHorizontal } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const deploymentOptions = [{
  title: "Dedicated Mac mini",
  description: "Deploy qOS on dedicated Apple Silicon hardware for maximum performance, physical isolation, and zero-compromise local processing.",
  icon: HardDrive,
  specs: [{
    label: "Latency",
    value: "Ultra-low"
  }, {
    label: "Privacy Score",
    value: "Maximum"
  }, {
    label: "Control Level",
    value: "Full Hardware"
  }]
}, {
  title: "Managed Multi-tenant",
  description: "Instant provisioning in our secure, managed cloud environment. Ideal for rapid scaling and team-wide agent access without hardware overhead.",
  icon: Cloud,
  specs: [{
    label: "Latency",
    value: "Optimized"
  }, {
    label: "Privacy Score",
    value: "High (Isolated)"
  }, {
    label: "Control Level",
    value: "Environment"
  }]
}, {
  title: "Custom Operator",
  description: "Integrate qOS directly into your existing secure infrastructure. Built for enterprise environments requiring bespoke networking and compliance.",
  icon: Terminal,
  specs: [{
    label: "Latency",
    value: "Variable"
  }, {
    label: "Privacy Score",
    value: "Custom"
  }, {
    label: "Control Level",
    value: "Absolute"
  }]
}];
export default function DeploymentEnvironments() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="deployment-environments" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">
            Deployment Architecture
          </h2>
          <p className="text-lg text-muted-foreground">
            Choose the operating environment that matches your security posture and performance requirements. From dedicated silicon to managed clusters.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {deploymentOptions.map((option, i) => {
          const Icon = option.icon;
          return <Card key={option.title} data-index={i} className="group relative flex flex-col h-full bg-card border-border transition-all duration-500 hover:-translate-y-1 hover:border-primary hover:shadow-[0_0_30px_color-mix(in_srgb,var(--color-primary)_20%,transparent)] overflow-hidden">
                {}
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-border group-hover:via-primary to-transparent transition-colors duration-500" />

                <CardHeader className="pb-4">
                  <div className="mb-4 inline-flex size-12 items-center justify-center rounded-lg border border-border/50 bg-gradient-to-br from-secondary/80 to-background shadow-inner relative overflow-hidden">
                    {}
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <Icon className="size-6 text-foreground/90 drop-shadow-md" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-xl tracking-tight text-foreground group-hover:text-primary transition-colors duration-300">
                    {option.title}
                  </CardTitle>
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground mt-2">
                    {option.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow">
                  {}
                </CardContent>

                <CardFooter className="flex-col items-start border-t border-border/50 pt-5 mt-auto bg-secondary/10">
                  <div className="w-full">
                    <p className="text-xs font-mono tracking-wider text-muted-foreground uppercase mb-3 flex items-center gap-2">
                      <Zap className="size-3 text-primary" />
                      Technical Specs
                    </p>
                    <div className="flex flex-col gap-2 w-full">
                      {option.specs.map((spec, specIndex) => <div key={specIndex} className="flex items-center justify-between w-full">
                          <span className="text-xs text-muted-foreground">{spec.label}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-mono bg-background/50 border-border/50">
                            {spec.value}
                          </Badge>
                        </div>)}
                    </div>
                  </div>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}